import React from 'react';
import {Table, TableContainer, Tbody, Tr} from "@chakra-ui/react";
import MainPageTableHeader from "./MainPageTableHeader.tsx";
import ProjectTableFileColumn from "./ProjectTableFileColumn.tsx";
import ProjectTableSourceColumn from "./ProjectTableSourceColumn.tsx";
import ProjectTableActionsColumn from "./ProjectTableActionsColumn.tsx";
import {IProject} from "../../models/interfaces/project.interface.ts";
import {useProject} from "../../hooks/useProject.ts";
import ProjectTableStateColumn from "./ProjectTableStateColumn.tsx";
import ProjectTableCreatedByColumn from "./ProjectTableCreatedByColumn.tsx";
import ProjectTableWorkflow from "./ProjectTableWorkflow.tsx";

interface ProjectTableProps {
  tableRef: React.RefObject<HTMLDivElement | null>;
  projects: IProject[];
  selectedRows: string[];
  toggleSelectedRow: (project: IProject) => void;
  setSelectedProjectId: (id: string | null) => void;
  onAlertDialogOpen: () => void;
  showProjectOverview: (projectId: string) => void;
  openFeedbackDrawer: (projectId: string, targets: string[]) => void;
  presentPreview: (projectId: string) => void;
  isDownloading: Record<string, boolean>;
  setIsDownloading: (update: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => void;
}

const ProjectTable: React.FC<ProjectTableProps> = (
  {
    tableRef,
    projects,
    selectedRows,
    toggleSelectedRow,
    setSelectedProjectId,
    onAlertDialogOpen,
    showProjectOverview,
    openFeedbackDrawer,
    presentPreview,
    isDownloading,
    setIsDownloading,
  }) => {
  const {calculateProjectPercentage} = useProject();

  const handleRowClick = (event: React.MouseEvent, project: IProject, projectPercentage: number | null) => {
    if (event.metaKey || event.ctrlKey) {
      if (projectPercentage !== 100) return;
      toggleSelectedRow(project);
    } else {
      showProjectOverview(project.id)
    }
  }

  return (
    <TableContainer
      ref={tableRef}
      className="flex-1 bg-n-200 dark:bg-n-600 rounded-lg border"
      sx={{flex: 1, minHeight: 0, overflowX: "hidden", overflowY: "auto"}}
    >
      <Table variant="simple">
        <MainPageTableHeader/>
        <Tbody>
          {projects.map((project: IProject, index: number) => {
            const projectPercentage: number | null = calculateProjectPercentage(project);

            return (
              <Tr
                key={index}
                className={`cursor-pointer ${
                  selectedRows.includes(project.id) ? "bg-n-800/50 hover:bg-n-800" : "hover:bg-n-500"}`}
                sx={{"& td": {borderBottom: "1px solid slateGray.500"}}}
                onClick={(event: React.MouseEvent) => handleRowClick(event, project, projectPercentage)}
              >
                <ProjectTableFileColumn
                  project={project}
                  selectedRows={selectedRows}
                  projectPercentage={projectPercentage}
                />
                <ProjectTableSourceColumn source={project.sourceLanguage}/>
                <ProjectTableStateColumn project={project}/>
                <ProjectTableWorkflow project={project}/>
                <ProjectTableCreatedByColumn createdById={project.createdBy}/>
                <ProjectTableActionsColumn
                  project={project}
                  setSelectedProjectId={setSelectedProjectId}
                  onAlertDialogOpen={onAlertDialogOpen}
                  openFeedbackDrawer={openFeedbackDrawer}
                  presentPreview={presentPreview}
                />
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}

export default ProjectTable;